import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { StaticAd } from './StaticAd';
import { reducedModeEnabled } from '../utils/reducedMode';
import { AdTypeKey, adTypeKeys, normalizeAdConfigDoc, normalizePlacementsDoc } from '../utils/adSystem';

interface QrOkeAdProps {
  show: boolean;
  onClose: () => void;
  adType?: AdTypeKey;
  // Cycle through multiple ad types while open
  cycle?: boolean;
  cycleTypes?: AdTypeKey[];
  cycleIntervalMs?: number; // default 6000ms
  // Placement context for reading admin/adPlacements
  placement?: 'viewerSplash' | 'hostBreak';
  // Control overall duration and auto-close behavior
  durationSeconds?: number; // default 10s (or 30s if cycle)
  disableAutoClose?: boolean; // if true, no auto-close; parent controls show
}

const QrOkeAd: React.FC<QrOkeAdProps> = ({ show, onClose, adType = 'house', cycle = false, cycleTypes, cycleIntervalMs = 6000, placement = 'viewerSplash', durationSeconds, disableAutoClose = false }) => {
  const navigate = useNavigate();
  const [displayedType, setDisplayedType] = useState<AdTypeKey>(adType);
  const [countdown, setCountdown] = useState<number>(durationSeconds ?? (cycle ? 30 : 10));
  const [adConfig, setAdConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [useStaticAd, setUseStaticAd] = useState(false);
  const cycleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!show) return;
    if (disableAutoClose) return; // parent-controlled; no countdown
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    // Auto-close when countdown hits zero
    const timer = setTimeout(onClose, 300);
    return () => clearTimeout(timer);
  }, [show, countdown, onClose, disableAutoClose]);

  useEffect(() => {
    if (show) {
      // Reset state on open
      setDisplayedType(adType);
      setCountdown(durationSeconds ?? (cycle ? 30 : 10));
      loadAdConfig();
    }
    return () => {
      // cleanup cycle timer
      if (cycleTimerRef.current) {
        clearInterval(cycleTimerRef.current);
        cycleTimerRef.current = null;
      }
    };
  }, [show, adType, cycle, durationSeconds]);

  const loadAdConfig = async () => {
    try {
      setLoading(true);
      const configRef = doc(db, 'admin', 'adConfig');
      const configSnap = await getDoc(configRef);
      const placementsSnap = await getDoc(doc(db, 'admin', 'adPlacements'));
      const config = normalizeAdConfigDoc(configSnap.exists() ? configSnap.data() : undefined);
      const placements = normalizePlacementsDoc(placementsSnap.exists() ? placementsSnap.data() : undefined);
      
      if (configSnap.exists()) {
        setAdConfig(config);

        // Determine which ad type(s) to show based on placements and props
        const placementData = placements[placement];
        const enabledFromPlacement: AdTypeKey[] | undefined = placementData?.enabledTypes;
        const rotationMsFromPlacement: number | undefined = placementData?.rotationSeconds ? placementData.rotationSeconds * 1000 : undefined;
        const allowedTypes = (cycleTypes && cycleTypes.length > 0)
          ? cycleTypes
          : (enabledFromPlacement && enabledFromPlacement.length > 0)
            ? enabledFromPlacement
            : adTypeKeys;
        const types = allowedTypes.filter((t) => !!config[t]);

        // Start cycling if requested
        if (cycle && types.length > 1) {
          let idx = Math.max(0, types.indexOf(adType as any));
          setDisplayedType(types[idx] as any);
          if (cycleTimerRef.current) {
            clearInterval(cycleTimerRef.current);
            cycleTimerRef.current = null;
          }
          const interval = Math.max(2000, rotationMsFromPlacement ?? cycleIntervalMs);
          cycleTimerRef.current = window.setInterval(() => {
            idx = (idx + 1) % types.length;
            setDisplayedType(types[idx] as any);
          }, interval) as unknown as number;
        } else {
          setDisplayedType(adType);
        }
      } else {
        // No config found, use static ad
        setUseStaticAd(true);
      }
    } catch (error) {
      console.error('Error loading ad config:', error);
      // Fallback to static ad on error
      setUseStaticAd(true);
    } finally {
      setLoading(false);
    }
  };

  // Update static/video decision whenever the displayed type or config changes
  useEffect(() => {
    if (!adConfig) return;
    const current = adConfig[displayedType];
    const hasValidVideo = current?.videoId && String(current.videoId).trim() !== '';
    const hasValidImage = current?.imageUrl && String(current.imageUrl).trim() !== '';
    setUseStaticAd(!(hasValidVideo || hasValidImage));
  }, [adConfig, displayedType]);

  const trackAdInteraction = async (action: 'view' | 'click' | 'conversion') => {
    try {
      if (!adConfig) return;
      
      const configRef = doc(db, 'admin', 'adConfig');
      const currentData = adConfig[adType];
      const updatedData = {
        ...adConfig,
        [adType]: {
          ...currentData,
          [action === 'view' ? 'views' : action === 'click' ? 'clicks' : 'conversions']: 
            (currentData[action === 'view' ? 'views' : action === 'click' ? 'clicks' : 'conversions'] || 0) + 1
        }
      };
      
      // Update local state
      setAdConfig(updatedData);
      
      // Note: In production, you'd want to batch these updates or use a more robust analytics system
    } catch (error) {
      console.error('Error tracking ad interaction:', error);
    }
  };

  // Track view when ad is shown
  useEffect(() => {
    if (show && !loading && !useStaticAd) {
      trackAdInteraction('view');
    }
  }, [show, loading, useStaticAd]);

  if (!show) return null;

  // Show loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading ad...</p>
        </div>
      </div>
    );
  }

  // Use static ad if no YouTube data available
  if (useStaticAd) {
    return <StaticAd type={displayedType} onClose={onClose} />;
  }

  const handleCTA = (href?: string) => {
    // Track click analytics
    trackAdInteraction('click');
    try {
      if (href && typeof href === 'string') {
        const isExternal = /^https?:\/\//i.test(href);
        if (isExternal) {
          window.open(href, '_blank', 'noopener');
        } else {
          navigate(href);
        }
      } else {
        navigate('/feedback?from=/party-ad');
      }
    } finally {
      onClose();
    }
  };

  // Get current ad data from admin config
  const currentAdData = adConfig?.[displayedType];
  const videoId = currentAdData?.videoId;
  const imageUrl = currentAdData?.imageUrl;
  const imageAlt = currentAdData?.imageAlt;
  const videoUrl = videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1` : null;

  const adContent = {
    house: {
      title: currentAdData?.title || (reducedModeEnabled ? '🎤 Help Shape What Returns Next' : '🚀 Ready to Level Up?'),
      subtitle: currentAdData?.description || (reducedModeEnabled
        ? 'QrOke keeps the core karaoke flow light. Tell us which tools would most improve your event.'
        : 'Unlimited queue • No time limits • Custom branding'),
      cta: currentAdData?.ctaText || (reducedModeEnabled ? 'Give Feedback / Request Feature' : 'Upgrade to Party Pass - Only $2'),
      ctaHref: currentAdData?.ctaHref || (reducedModeEnabled ? '/feedback?from=/party-ad' : '/subscription'),
      color: currentAdData?.color || 'from-purple-600 to-blue-600'
    },
    lean: {
      title: currentAdData?.title || (reducedModeEnabled ? '⚡ Built For The Fast Path' : '🎤 Professional Karaoke Features'),
      subtitle: currentAdData?.description || (reducedModeEnabled
        ? 'QR join, lightweight search, live queue, and playback display stay front and center.'
        : 'External displays • Audio routing • Queue management'),
      cta: currentAdData?.ctaText || (reducedModeEnabled ? 'See Setup Notes' : 'See All Pro Features'),
      ctaHref: currentAdData?.ctaHref || (reducedModeEnabled ? '/instructions#lean-mode' : '/subscription'),
      color: currentAdData?.color || 'from-green-600 to-teal-600'
    },
    sponsor: {
      title: currentAdData?.title || (reducedModeEnabled ? '📣 Sponsor This Break' : '⭐ Loved by Venues & DJs'),
      subtitle: currentAdData?.description || (reducedModeEnabled
        ? 'Free-tier breaks can carry house promos or local sponsor spots that keep QrOke running without paywalls.'
        : '\"QrOke transformed our karaoke nights!\" - Mike\'s Bar'),
      cta: currentAdData?.ctaText || (reducedModeEnabled ? 'Contact About Sponsorship' : 'Join Thousands of Happy Hosts'),
      ctaHref: currentAdData?.ctaHref || (reducedModeEnabled
        ? 'mailto:bkentner@gmail.com?subject=QrOke%20Sponsor%20Inquiry&body=Hi%2C%20I%27m%20interested%20in%20sponsoring%20QrOke%20free-tier%20ad%20placements.'
        : '/subscription'),
      color: currentAdData?.color || 'from-orange-600 to-red-600'
    }
  };

  const content = adContent[displayedType];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header with countdown */}
        <div className="flex justify-between items-center p-4 bg-gray-100">
          <div className="flex items-center space-x-2">
            <img src="/Qroke Logo.png" alt="QrOke" className="h-8" />
            <span className="font-bold text-gray-800">QrOke</span>
          </div>
          <div className="flex items-center space-x-3">
            {!disableAutoClose && countdown > 0 && (
              <span className="text-sm text-gray-600">
                Ad closes in {countdown}s
              </span>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className={`bg-gradient-to-br ${content.color} text-white p-8 text-center`}>
          <h2 className="text-3xl font-bold mb-2">{content.title}</h2>
          <p className="text-xl mb-6 opacity-90">{content.subtitle}</p>
          
          {/* Creative block: image > video > fallback */}
          <div className="bg-black rounded-lg mb-6 overflow-hidden">
            {imageUrl ? (
              <img src={imageUrl} alt={imageAlt || content.title} className="w-full max-h-[320px] object-contain bg-black" />
            ) : videoUrl ? (
              <iframe
                width="100%"
                height="300"
                src={videoUrl}
                title="QrOke Promotional Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full"
              />
            ) : (
              <div className="p-8 text-center">
                <div className="text-6xl mb-4">🖼️</div>
                <p className="text-lg opacity-80">
                  Promotional Creative
                </p>
                <p className="text-sm opacity-60 mt-2">
                  (No video or image configured)
                </p>
              </div>
            )}
          </div>

          <button
            onClick={() => handleCTA((content as any).ctaHref)}
            className="bg-white text-gray-800 font-bold py-4 px-8 rounded-lg text-lg hover:bg-gray-100 transition-colors shadow-lg"
          >
            {content.cta}
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 text-center">
          <p className="text-sm text-gray-600">
            {reducedModeEnabled
              ? 'Free sessions may include house promos or sponsor spots that support the lean version of QrOke'
              : 'Free users see promotional content • Upgrade for ad-free experience'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QrOkeAd;